import React, { useEffect, useState } from "react";
import { Popover } from '@headlessui/react';
import snakeToPascal, { isFloat, round } from "../../../tools";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { useAppStore } from "../../../shared/stores/useAppStore";
import MathJaxComponent from "./MathJax";
import FieldDependenciesPanel from "./reusables/FieldDependenciesPanel";

// Removed StyledDataGrid - using custom table with Tailwind

const formatTimeSerieObj = (ts) => {
    const ret = {
        name: ts.name,
        field_id: ts.field_id,
        roundable: ts.roundable,
        is_percentage: ts.is_percentage,
        compute_error: ts.compute_error,
    }

    ts.data.map(e => {
        ret[e.date.value + '|' + e.end_of_period.value] = e.value.value
    })
    return ret
}

const renderNameCell = (value) => {
    return (
        <span className="font-semibold">{snakeToPascal(value.row[value.field])}</span>
    )
}

const renderDateHeader = (value) => {
    const [bop, eop] = value.split('|');
    return (
        <div className="flex flex-col">
            <span className="font-medium text-slate-500">
                {dayjs(bop).format(DATESYSTEM_TO_FORMAT[useAppStore.getState().userSettings.dateSystem])}
            </span>
            <span className="font-medium text-slate-500">
                {dayjs(eop).format(DATESYSTEM_TO_FORMAT[useAppStore.getState().userSettings.dateSystem])}
            </span>
        </div>
    )
}

const renderCDHeader = () => {
    return (
        <div className="flex flex-col items-center">
            <span className="font-medium text-slate-500">
                Computing
            </span>
            <span className="font-medium text-slate-500">
                Details
            </span>
        </div>
    )
}

const renderCEHeader = () => {
    return (
        <div className="flex flex-col items-center">
            <span className="font-medium text-slate-500">
                Compute
            </span>
            <span className="font-medium text-slate-500">
                Errors
            </span>
        </div>
    )
}


const renderNameHeader = () => {

    return (
        <div className="flex flex-col">
            <span className="font-medium text-slate-500" title='Beginning of Period'>
                Start
            </span>
            <span className="font-medium text-slate-500" title='End of Period'>
                End
            </span>
        </div>
    )
}

const renderComputeErrors = (value) => {
    if (value.row[value.field]) {
        return (
            <Popover className="relative">
                <Popover.Button className="w-[50px] h-[30px] rounded-md bg-red-600 text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 flex items-center justify-center">
                    <span className="material-symbols-outlined text-[20px]">warning</span>
                </Popover.Button>
                <Popover.Panel className="absolute z-10 mt-2 w-[230px] bg-white rounded-lg shadow-lg border border-slate-200">
                    <div className="h-[200px] overflow-y-auto p-4">
                        <span className="material-symbols-outlined text-[35px] text-red-600 block text-center mb-2">warning</span>
                        <p className="text-sm">{value.row[value.field]}</p>
                    </div>
                </Popover.Panel>
            </Popover>
        )
    } else {
        return null;
    }
}

const renderComputingDetails = (value) => {
    return (
        <Popover className="relative">
            <Popover.Button className="w-[50px] h-[30px] rounded-md bg-teal-500 text-white hover:bg-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px]">assignment</span>
            </Popover.Button>
            <Popover.Panel className="absolute z-10 mt-2 w-fit max-w-2xl bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="h-[300px] overflow-y-auto">
                    <h3 className="text-center text-xl font-bold p-2">{snakeToPascal(value.row['name'])}</h3>
                    {
                        value.row[value.field] ? value.row[value.field].split('\n').map((str, idx) => {
                            if (str) {
                                if (str[0] == '|') {
                                    return(
                                        <MathJaxComponent key={idx} latex={str.substring(1)}/>
                                    )
                                } else {
                                    return (
                                        <p key={idx} className="px-2">
                                            {str}
                                        </p>
                                    )
                                }

                            } else {
                                return <br key={idx}></br>
                            }
                        }) : <div></div>
                    }
                </div>
            </Popover.Panel>
        </Popover>
    )

}

const getLonguest = (timeSerieDatas) => {
    let longuest = {};

    timeSerieDatas.map(ts => {
        if (Object.keys(ts).length > Object.keys(longuest).length) {
            longuest = ts
        }
    })

    return longuest;
}

const hasComputeErrors = (fields) => {
    let hasErrors = false;

    Object.values(fields).map((field: any) => {
        if (field.compute_error) {
            hasErrors = true;
        }
    });

    return hasErrors
}

export default function OutputTimeSeriesGridOperational({ fields, containerClassName = undefined, scenarioId = null, onNavigate = undefined }) {
    const [depsFieldId, setDepsFieldId] = useState<number | null>(null);
    const [depsFieldName, setDepsFieldName] = useState<string>('');

    const timeSerieFields = [];

    Object.entries(fields).filter(([name, field]: any) => {
        if (field.type == 'time_serie') {
            timeSerieFields.push({
                name: field.display_name ? field.display_name : name,
                field_id: field.id,
                roundable: field.roundable,
                is_percentage: field.is_percentage,
                compute_error: field.compute_error,
                data: field.currentValue.value.data
            });
        }
    });
    
    const timeSerieData = timeSerieFields.map(ts => formatTimeSerieObj(ts))

    const renderValueCell = (value) => {
        const rawValue = value.row[value.field];

        if (rawValue == null || rawValue == undefined) {
            return '';
        }

        const valueToFormat = value.row['is_percentage'] && isFloat(rawValue) ? rawValue * 100 : rawValue;
        const suffix = value.row['is_percentage'] && isFloat(rawValue) ? ' %' : '';

        if (isFloat(valueToFormat) && value.row['roundable']) {
            return round(valueToFormat, useAppStore.getState().userSettings.roundPrecision).toLocaleString(useAppStore.getState().userSettings.numberFormattingLocale, {maximumFractionDigits: useAppStore.getState().userSettings.roundPrecision}) + suffix;
        } else if (isFloat(valueToFormat)) {
            return valueToFormat.toLocaleString(useAppStore.getState().userSettings.numberFormattingLocale) + suffix;
        } else {
            return valueToFormat;
        }
    }

    if (timeSerieData.length == 0) {
        return null;
    }

    const longuestTsData = getLonguest(timeSerieData)

    const columns = [
        {
          field: 'name',
          headerName: '',
          width: 150,
          pinned: 'left',  // This locks the first column
          renderCell: (value) => renderNameCell(value),
          renderHeader: () => renderNameHeader(),
          disableReorder: true,
          disableColumnMenu: true,
          filterable: false,
          resizable: false,
          sortable: false,
          cellClassName: 'leftmost-column'
        },
      ];

      if (scenarioId) {
        // @ts-ignore
        columns.push({
          field: 'dependencies',
          headerName: '',
          width: 40,
          renderHeader: () => null,
          renderCell: (value) => (
              <button
                  onClick={() => {
                      setDepsFieldId(value.row.field_id);
                      setDepsFieldName(value.row.name);
                  }}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                  title="View dependencies"
              >
                  <span className="material-symbols-outlined text-[16px]">device_hub</span>
              </button>
          ),
          disableReorder: true,
          disableColumnMenu: true,
          filterable: false,
          resizable: false,
          sortable: false,
        });
      }

      if (hasComputeErrors(fields)) {
        columns.push( // @ts-ignore
            {
                field: 'compute_error',
                headerName: 'Compute Errors',
                renderHeader: () => renderCEHeader(),
                width: 85,
                disableReorder: true,
                disableColumnMenu: true,
                filterable: false,
                resizable: false,
                sortable: false,
                renderCell: (value) => renderComputeErrors(value),
            }
        )
      }
    
      if (timeSerieData) {
        // @ts-ignore
        columns.push(...Object.keys(longuestTsData)
            .filter(key => key !== 'name' && key !== 'field_id' && key !== 'compute_error' && key !== 'roundable' && key !== 'is_percentage')
            .map(key => ({
                field: key,
                headerName: key,
                renderHeader: () => renderDateHeader(key),
                width: 120,
                // valueFormatter: (value) => formatValue(value),
                display: 'text',
                disableReorder: true,
                disableColumnMenu: true,
                filterable: false,
                resizable: false,
                sortable: false,
                renderCell: (value) => renderValueCell(value),
            })
          )
        )
      }

    return (
        <div className={containerClassName ?? "mr-2 h-[600px] overflow-auto relative z-0"}>
            {scenarioId && (
                <FieldDependenciesPanel
                    isOpen={depsFieldId !== null}
                    onClose={() => setDepsFieldId(null)}
                    scenarioId={scenarioId}
                    fieldId={depsFieldId}
                    fieldName={depsFieldName}
                    onNavigate={onNavigate}
                />
            )}
            <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 sticky top-0 z-20">
                    <tr className="border-b border-slate-200">
                        {columns.map((col, idx) => (
                            <th
                                key={idx}
                                className={`p-2 text-xs font-medium text-slate-500 ${idx === 0 ? 'text-left sticky left-0 z-30 bg-slate-50 min-w-[160px]' : 'text-center min-w-[100px]'}`}
                                style={{ width: col.width }}
                            >
                                {col.renderHeader ? col.renderHeader() : col.headerName}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {timeSerieData.map((row) => (
                        <tr key={row.name} className="border-b border-slate-200 hover:bg-slate-50">
                            {columns.map((col, colIdx) => (
                                <td
                                    key={colIdx}
                                    className={`p-2 text-center ${colIdx === 0 ? 'text-left font-semibold text-slate-700 sticky left-0 z-10 bg-white border-r border-slate-100' : ''}`}
                                >
                                    {col.renderCell
                                        ? col.renderCell({ row, value: row[col.field], field: col.field })
                                        : row[col.field]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
